import { FC, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Chip,
    Fade,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    Menu,
    MenuItem,
    Select,
    Stack,
    Switch,
} from '@mui/material';
import { SCUIBreadCrumb, SCUICustomDataGrid, SCUIGeneralDrawer, SCUISnackbar } from '../../atomic/molecules';
import * as InstallmentHelper from './Installment.helper';
import { SCUIButton } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import billingInfoImage from '../../../src/assets/icons/svg/pageIcons/billingInfo.svg';
import { useTranslation } from 'react-i18next';
import { SCUIInstallmentFilter } from './components/SCUIInstallmentFilter/SCUIInstallmentFilter';
import { GeneralQuery, PaymentQuery } from '../../services/query';
import { PaymentCommand } from '../../services/command';
import { enumInstallmentStatus, enumSnackbarStatusType, PageUrlRo } from '../../types/enums';
import { Add, FilterAlt, MoreVert } from '@mui/icons-material';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { SCUIConfirmDialog } from './components/SCUIConfirmDialog/SCUIConfirmDialog';
import { SCUIInstallmentInsert } from './components/SCUIInstallmentInsert/SCUIInstallmentInsert';
import { SCUIInstallmentEdit } from './components/SCUIInstallmentEdit/SCUIInstallmentEdit';
import { IOM } from './installmentOptionManagement.classes';

export const InstallmentOptionManagement: FC<any> = (_props: any) => {
    const { t } = useTranslation();

    const [filterInput, setFilterInput] = useState<any>(InstallmentHelper.initialState.installmentFilters);

    const [loading, setLoading] = useState<any>(false);
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const [openInsert, setOpenInsert] = useState<boolean>(false);
    const [openEdit, setOpenEdit] = useState<boolean>(false);

    const [applySearch, setApplySearch] = useState<boolean>();

    const [countries, setCountries] = useState<InstallmentHelper.ISelectItem[]>([]);
    const [statuses, setStatuses] = useState<InstallmentHelper.ISelectItem[]>([]);

    const [searched, setSearched] = useState<boolean>(false);
    const [data, setData] = useState<any>([]);
    const [selectedData, setSelectedData] = useState<any>(InstallmentHelper.initialState.installmentFilters);

    const [filterChipList, setFilterChipList] = useState<any>([]);

    const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
    const [confirmationProperties, setConfirmationProperties] = useState<InstallmentHelper.IConfirmationProps>();

    const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
    const [snackbarProperties, setSnackbarProperties] = useState<InstallmentHelper.ISnackbarProps>();

    const getCountryList = async () => {
        let languageCode = localStorage.getItem('lan');
        const countryList = await GeneralQuery.GetRegionalCountryList(languageCode);

        let result: InstallmentHelper.ISelectItem[] = [];
        if (countryList && countryList.data.length > 0) {
            result = countryList.data.map((item: any) => ({
                id: item.countryId,
                name: item.countryName,
            }));
        }
        setCountries(result);
    };

    const getStatusList = () => {
        const data: InstallmentHelper.ISelectItem[] = [
            { id: 1, name: t('general.active') },
            { id: 2, name: t('general.passive') },
        ];
        setStatuses(data);
    };

    const search = async () => {
        if (!loading) {
            if (filterInput.country == null) {
                setShowSnackbar(true);
                setSnackbarProperties({
                    show: true,
                    autoHideDuration: 2000,
                    message: t('general.pleaseSelectCountry'),
                    status: enumSnackbarStatusType.warning,
                });
            } else {
                setLoading(true);

                let numberOfInstallments = filterInput.numberOfInstallment?.map((x: any) => x.name);

                const planList: any = await PaymentQuery.GetCreditCardTypePaymentPlans({
                    countryId: filterInput.country?.id ?? null,
                    creditCardTypeId: filterInput.creditCardType?.id ?? null,
                    numberOfInstallment: numberOfInstallments ?? null,
                    status: filterInput.status?.id ?? 0,
                });

                setData(planList.data);
                setLoading(false);
                setSearched(true);
                renderChipList();
            }
        }

        setApplySearch(false);
        setOpenFilter(false);
    };

    useEffect(() => {
        getCountryList();
        getStatusList();
    }, []);

    useEffect(() => {
        if (filterInput.country != null && filterInput.country != undefined) {
            search();
        }
    }, [applySearch]);

    const renderChipList = () => {
        let filters: any = [];
        if (filterInput.country?.id != undefined) {
            filters.push({
                name: t('general.country'),
                value: filterInput.country.name,
                type: 'country',
                id: filterInput.country.id,
            });
        }
        if (filterInput.creditCardType?.id != undefined) {
            filters.push({
                name: t('general.bank'),
                value: filterInput.creditCardType.name,
                type: 'creditCardType',
                id: filterInput.creditCardType.id,
            });
        }
        if (filterInput.numberOfInstallment?.length > 0) {
            if (filterInput.numberOfInstallment) {
                filterInput.numberOfInstallment.map((item: any) => {
                    filters.push({
                        name: t('installmentOptionManagement.installmentInformation'),
                        value: item.name,
                        type: 'numberOfInstallment',
                        id: item.id,
                    });
                });
            }
        }
        if (filterInput.status?.id != undefined) {
            filters.push({
                name: t('general.status'),
                value: filterInput.status.name,
                type: 'status',
                id: filterInput.status.id,
            });
        }

        setFilterChipList(filters);
    };

    const newInstallmentFilter = () => {
        let filterItems = InstallmentHelper.initialState;
        filterItems.installmentFilters['country'] = null;
        filterItems.installmentFilters['creditCardType'] = null;
        filterItems.installmentFilters['numberOfInstallment'] = [];
        filterItems.installmentFilters['status'] = null;
        return filterItems;
    };

    const handleFilter = (chipList: any) => {
        let filterItems = newInstallmentFilter();
        if (chipList) {
            chipList.map((item: any) => {
                let chipValue = { id: item.id, name: item.value };
                if (item.type == 'numberOfInstallment') {
                    filterItems.installmentFilters[item.type].push(chipValue);
                } else {
                    filterItems.installmentFilters[item.type] = chipValue;
                }
            });
        }

        setFilterInput(filterItems.installmentFilters);

        setApplySearch(true);
    };

    const handleChipDelete = (item: any) => {
        let filteredChipList = filterChipList.filter((x: any) => x != item);
        setFilterChipList(filteredChipList);
        handleFilter(filteredChipList);
    };

    const handleDelete = (item: any) => {
        setOpenConfirmationDialog(true);
        setConfirmationProperties({
            acceptClick: () => {
                handleDeleteAccept(item);
            },
            rejectClick: () => {
                setOpenConfirmationDialog(false);
            },
            title: t('general.deleteConfirmationText'),
            content: t('general.theSelectedLineWillBePermanentlyDeletedAfterConfirmation'),
            acceptButtonName: t('general.confirm'),
            rejectButtonName: t('general.cancel'),
        });
    };

    const handleDeleteAccept = async (item: any) => {
        let response = await PaymentCommand.DeleteInstallmentManagement({
            creditCardTypeId: item.creditCardTypeId,
        });

        let message = '';
        let snackbarStatus: enumSnackbarStatusType = enumSnackbarStatusType.info;
        if (response) {
            message = t('installmentOptionManagement.bankInformationDeleted');
            snackbarStatus = enumSnackbarStatusType.success;

            search();
        } else {
            message = t('general.anErrorOccured');
            snackbarStatus = enumSnackbarStatusType.warning;
        }

        setSnackbarProperties({
            show: true,
            autoHideDuration: 2000,
            message: message,
            status: snackbarStatus,
        });

        setShowSnackbar(true);
        setOpenConfirmationDialog(false);
    };

    const openEditInstallment = (row: any) => {
        setSelectedData({
            country: {
                id: row.countryId,
                name: row.countryKey,
            },
            creditCardType: {
                id: row.creditCardTypeId,
                name: row.creditCardTypeName,
            },
            numberOfInstallment:
                row.existInstallments.map((x: any) => ({
                    id: x.creditCardTypePaymentPlanId,
                    name: x.numberOfInstallments,
                })) ?? [],
            status: { id: row.status },
        });
        setOpenEdit(true);
    };

    const DataList = () => {
        const columns: ExtendedGridColDef[] = [
            {
                field: 'status',
                headerName: t('general.status'),
                flex: 0.5,
                renderCell: (cellInfo) => {
                    const isActive = cellInfo.row.status == enumInstallmentStatus.active;
                    return (
                        <div className={IOM.cellStatus}>
                            <Switch
                                className={IOM.inputCellStatusSwitch}
                                data-credit-card-type-id={cellInfo.row.creditCardTypeId}
                                data-status={String(isActive)}
                                data-status-id={cellInfo.row.status}
                                checked={isActive}
                            />
                        </div>
                    );
                },
                filterable: false,
                disableReorder: false,
                sortable: false,
                hideable: false,
                editable: false,
                resizable: false,
                groupable: false,
                disableColumnMenu: true,
            },
            {
                field: 'creditCardTypeId',
                headerName: 'ID',
                flex: 0.5,
                renderCell: (params: any) => (
                    <span
                        className={IOM.cellCreditCardTypeId}
                        data-credit-card-type-id={params.row.creditCardTypeId}
                    >
                        {params.row.creditCardTypeId}
                    </span>
                ),
            },
            {
                field: 'regionNameKey',
                headerName: t('general.country'),
                flex: 1,
                renderCell: (params: any) => (
                    <span
                        className={IOM.cellRegionName}
                        data-credit-card-type-id={params.row.creditCardTypeId}
                        data-region-name-key={params.row.regionNameKey}
                    >
                        {params.row.regionNameKey}
                    </span>
                ),
            },
            {
                field: 'creditCardTypeName',
                headerName: t('general.bank'),
                flex: 1,
                renderCell: (params: any) => (
                    <span
                        className={IOM.cellCreditCardTypeName}
                        data-credit-card-type-id={params.row.creditCardTypeId}
                        data-credit-card-type-name={params.row.creditCardTypeName}
                    >
                        {params.row.creditCardTypeName}
                    </span>
                ),
            },
            {
                field: 'existInstallments',
                headerName: t('installmentOptionManagement.installmentInformation'),
                flex: 3,
                renderCell: (params) => {
                    return (
                        <div
                            className={IOM.cellInstallments}
                            data-credit-card-type-id={params.row.creditCardTypeId}
                        >
                            {params.value &&
                                params.value.map((item: any) => {
                                    return (
                                        <Chip
                                            key={item.creditCardTypePaymentPlanId}
                                            className={IOM.cellInstallmentChip}
                                            data-installment-plan-id={item.creditCardTypePaymentPlanId}
                                            data-number-of-installments={item.numberOfInstallments}
                                            label={
                                                item.numberOfInstallments == '0'
                                                    ? t('installmentOptionManagement.singlePayment')
                                                    : item.numberOfInstallments
                                            }
                                            sx={{ mr: 1 }}
                                        />
                                    );
                                })}
                        </div>
                    );
                },
            },
            {
                field: 'actions ',
                headerName: '',
                align: 'right',
                flex: 0.5,
                filterable: false,
                disableReorder: false,
                sortable: false,
                hideable: false,
                editable: false,
                resizable: false,
                groupable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    return (
                        <PopupState variant="popover" popupId="demo-popup-menu">
                            {(popupState: any) => (
                                <div className={IOM.cellActions}>
                                    <IconButton
                                        {...bindTrigger(popupState)}
                                        className={IOM.btnRowMenu}
                                        data-credit-card-type-id={params.row.creditCardTypeId}
                                    >
                                        <MoreVert className="svg_icons" />
                                    </IconButton>
                                    <Menu {...bindMenu(popupState)} className={IOM.rowMenu}>
                                        <MenuItem
                                            className={`${IOM.rowMenuItem} ${IOM.rowMenuItemEdit}`}
                                            data-action="edit"
                                            data-credit-card-type-id={params.row.creditCardTypeId}
                                            onClick={() => openEditInstallment(params.row)}
                                        >
                                            {t('general.edit')}
                                        </MenuItem>
                                        {params.row.status == enumInstallmentStatus.active && (
                                            <MenuItem
                                                className={`${IOM.rowMenuItem} ${IOM.rowMenuItemDelete}`}
                                                data-action="delete"
                                                data-credit-card-type-id={params.row.creditCardTypeId}
                                                onClick={() => handleDelete(params.row)}
                                            >
                                                {t('general.delete')}
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </div>
                            )}
                        </PopupState>
                    );
                },
            },
        ];

        return (
            <Grid container direction="row" spacing={2} className={IOM.dataList}>
                <Grid
                    className={IOM.dataListTitleBox}
                    size={{
                        sm: 8,
                        md: 8,
                        lg: 8,
                        xl: 8,
                    }}
                >
                    <p className={IOM.dataListTitle} style={{ fontWeight: 'bold', marginTop: 10, marginBottom: 10 }}>
                        {t('installmentOptionManagement.bankInstallmentInformation')}
                    </p>
                </Grid>
                <Grid
                    className={IOM.toolbarActions}
                    size={{
                        sm: 4,
                        md: 4,
                        lg: 4,
                        xl: 4,
                    }}
                >
                    <IconButton
                        className={IOM.btnAddNew}
                        sx={{
                            float: 'right',
                            width: 100,
                            height: 40,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            marginRight: 1,
                        }}
                        onClick={() => setOpenInsert(true)}
                        disabled
                    >
                        <Button className={IOM.btnAddNewInner}>
                            <Add className="svg_icons" sx={{ color: 'primary.main' }} />
                            {t('general.addNew')}
                        </Button>
                    </IconButton>
                    <IconButton
                        className={IOM.btnOpenFilter}
                        sx={{
                            float: 'right',
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            marginRight: 1,
                        }}
                        onClick={() => setOpenFilter(true)}
                    >
                        <FilterAlt className="svg_icons" sx={{ color: 'primary.main' }} />
                    </IconButton>
                    <FormControl
                        className={IOM.toolbarStatusControl}
                        sx={{ mr: 1, minWidth: 120, float: 'right' }}
                        size="small"
                    >
                        <InputLabel id="labelStatus">{t('general.status')}</InputLabel>
                        <Select
                            className={IOM.inputToolbarStatus}
                            labelId="labelStatus"
                            id="parentMenuLabel"
                            value={filterInput.status?.id}
                            label={t('general.status')}
                            onChange={(event) => {
                                const { value } = event.target;
                                setFilterInput({
                                    ...filterInput,
                                    status:
                                        value != '' && value != undefined
                                            ? {
                                                  id: value,
                                                  name: statuses.find((x) => x.id == value)?.name,
                                              }
                                            : null,
                                });

                                if (filterInput.status != value && !(value == '' && filterInput.status == null)) {
                                    setApplySearch(true);
                                }
                            }}
                        >
                            <MenuItem
                                value=""
                                className={`${IOM.toolbarStatusOption} ${IOM.toolbarStatusOptionEmpty}`}
                                data-status-id=""
                            >
                                {t('general.select')}
                            </MenuItem>
                            {statuses.length > 0 &&
                                statuses.map((item: any) => {
                                    return (
                                        <MenuItem
                                            value={item.id}
                                            key={item.id}
                                            className={IOM.toolbarStatusOption}
                                            data-status-id={item.id}
                                            data-status-name={item.name}
                                        >
                                            {item.name}
                                        </MenuItem>
                                    );
                                })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid
                    className={IOM.chipsLabelBox}
                    style={{ display: filterChipList.length > 0 ? 'block' : 'none' }}
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <p className={IOM.chipsLabel} style={{ marginTop: 5, marginBottom: 10 }}>
                        {t('installmentOptionManagement.appliedFilters')}
                    </p>
                </Grid>
                <Grid
                    className={IOM.chipsBox}
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    {
                        <Stack direction="row" spacing={1} className={IOM.chipsStack}>
                            {filterChipList &&
                                filterChipList.map((chipItem: any, idx: number) => {
                                    let labelText = '';
                                    if (chipItem.type == 'numberOfInstallment' && chipItem.value == 0) {
                                        let value = t('installmentOptionManagement.singlePayment');
                                        labelText = `${chipItem.name} : ${value}`;
                                    } else {
                                        labelText = `${chipItem.name} : ${chipItem.value}`;
                                    }

                                    const typeClass =
                                        chipItem.type === 'country'
                                            ? IOM.chipCountry
                                            : chipItem.type === 'creditCardType'
                                            ? IOM.chipCreditCardType
                                            : chipItem.type === 'numberOfInstallment'
                                            ? IOM.chipNumberOfInstallment
                                            : chipItem.type === 'status'
                                            ? IOM.chipStatus
                                            : '';

                                    return chipItem.type == 'country' ? (
                                        <Chip
                                            key={`${chipItem.type}-${chipItem.id}-${idx}`}
                                            label={`${labelText}`}
                                            className={`${IOM.chip} ${typeClass}`}
                                            data-chip-type={chipItem.type}
                                            data-chip-id={chipItem.id}
                                            data-chip-value={chipItem.value}
                                        />
                                    ) : (
                                        <Chip
                                            key={`${chipItem.type}-${chipItem.id}-${idx}`}
                                            label={`${labelText}`}
                                            className={`${IOM.chip} ${typeClass}`}
                                            data-chip-type={chipItem.type}
                                            data-chip-id={chipItem.id}
                                            data-chip-value={chipItem.value}
                                            onDelete={() => {
                                                handleChipDelete(chipItem);
                                            }}
                                        />
                                    );
                                })}
                        </Stack>
                    }
                </Grid>
                <Grid
                    className={IOM.grid}
                    size={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    <SCUICustomDataGrid randomId rows={data} columns={columns} />
                </Grid>
            </Grid>
        );
    };

    return (
        <Box className={IOM.root}>
            <Box className={IOM.loading}>
                <Fade in={loading} unmountOnExit>
                    <LinearProgress />
                </Fade>
            </Box>

            <SCUIContent className={IOM.page}>
                <Grid container direction="row" spacing={2} className={IOM.breadcrumb}>
                    <SCUIBreadCrumb
                        title={t('installmentOptionManagement.payuInstallmentManagement')}
                        componentLink={PageUrlRo.installment.url}
                    />
                </Grid>

                {!searched ? (
                    <div className={IOM.emptyState}>
                        <div
                            className={IOM.emptyInfo}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <img src={billingInfoImage} className={`centeredImg ${IOM.emptyImage}`} />
                            <p
                                className={IOM.emptyText}
                                style={{
                                    textAlign: 'center',
                                    marginTop: '3rem',
                                    maxWidth: '450px',
                                }}
                            >
                                {t('installmentOptionManagement.noRecordText')}
                            </p>
                        </div>
                        <div
                            className={IOM.emptyForm}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignContent: 'center',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <FormControl className={IOM.emptyCountryField} style={{ width: 450, margin: '20px' }}>
                                <InputLabel id="labelCountry">{t('general.pleaseSelectCountry')}</InputLabel>
                                <Select
                                    className={IOM.inputEmptyCountry}
                                    labelId="labelCountry"
                                    id="parentMenuLabel"
                                    name="selectCountry"
                                    value={filterInput.country?.id}
                                    label={t('general.pleaseSelectCountry')}
                                    onChange={(event: any) => {
                                        const { value } = event.target;
                                        setFilterInput({
                                            ...filterInput,
                                            country: {
                                                id: value,
                                                name: countries.find((x) => x.id == value)?.name,
                                            },
                                        });
                                    }}
                                >
                                    {countries.length > 0 &&
                                        countries.map((item: any) => {
                                            return (
                                                <MenuItem
                                                    value={item.id}
                                                    key={item.id}
                                                    className={IOM.emptyCountryOption}
                                                    data-country-id={item.id}
                                                    data-country-name={item.name}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                            </FormControl>

                            <FormControl className={IOM.emptyFilterAction} style={{ width: 450, margin: '10px' }}>
                                <SCUIButton
                                    className={IOM.btnEmptyFilter}
                                    text={t('general.filter')}
                                    type="primary"
                                    sizeType="default"
                                    size="m"
                                    onClick={search}
                                />
                            </FormControl>

                            <FormControl className={IOM.emptyDetailedAction} style={{ width: 450 }}>
                                <SCUIButton
                                    className={IOM.btnEmptyDetailed}
                                    text={t('general.detailedFiltering')}
                                    type="ghost"
                                    sizeType="default"
                                    size="m"
                                    onClick={() => setOpenFilter(true)}
                                />
                            </FormControl>
                        </div>
                    </div>
                ) : (
                    <DataList />
                )}
            </SCUIContent>

            {snackbarProperties && (
                <SCUISnackbar
                    show={showSnackbar}
                    messageText={snackbarProperties?.message}
                    status={snackbarProperties?.status ?? enumSnackbarStatusType.info}
                    autoHideDuration={snackbarProperties?.autoHideDuration ?? 1000}
                    onClose={() => {
                        setShowSnackbar(false);
                    }}
                />
            )}

            {confirmationProperties && (
                <SCUIConfirmDialog
                    open={openConfirmationDialog}
                    acceptClick={confirmationProperties?.acceptClick}
                    rejectClick={confirmationProperties?.rejectClick}
                    title={confirmationProperties?.title}
                    content={confirmationProperties?.content ?? ''}
                    acceptButtonName={t('general.confirm')}
                    rejectButtonName={t('general.cancel')}
                />
            )}

            <Box className={IOM.filterDrawer}>
                <SCUIGeneralDrawer
                    name="DetailFilterDrawer"
                    open={openFilter}
                    width={'450px'}
                    onClose={() => {
                        setOpenFilter(false);
                    }}
                >
                    <SCUIInstallmentFilter
                        countryList={countries}
                        filterInput={filterInput}
                        setFilterInput={setFilterInput}
                        search={search}
                        onClose={() => setOpenFilter(false)}
                    />
                </SCUIGeneralDrawer>
            </Box>

            <Box className={IOM.insertDrawer}>
                <SCUIGeneralDrawer
                    name="InsertDrawer"
                    open={openInsert}
                    width={'450px'}
                    onClose={() => {
                        setOpenInsert(false);
                    }}
                >
                    <SCUIInstallmentInsert
                        setShowSnackbar={setShowSnackbar}
                        setSnackbarProperties={setSnackbarProperties}
                        search={search}
                        onClose={() => {
                            setOpenInsert(false);
                        }}
                    />
                </SCUIGeneralDrawer>
            </Box>

            <Box className={IOM.editDrawer}>
                <SCUIGeneralDrawer
                    name="EditDrawer"
                    open={openEdit}
                    width={'450px'}
                    onClose={(_event: any, reason: any) => {
                        if (reason === 'backdropClick') {
                            setOpenEdit(false);
                        }
                    }}
                >
                    <SCUIInstallmentEdit
                        selectedData={selectedData}
                        setShowSnackbar={setShowSnackbar}
                        setSnackbarProperties={setSnackbarProperties}
                        setOpenConfirmationDialog={setOpenConfirmationDialog}
                        setConfirmationProperties={setConfirmationProperties}
                        search={search}
                        onClose={() => {
                            setOpenEdit(false);
                        }}
                    />
                </SCUIGeneralDrawer>
            </Box>
        </Box>
    );
};
